import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../../ThemeContext';
import './SimpleTimePicker.css';

const generateTimeSlots = () => {
  const slots = [];
  let hour = 5;
  let period = 'AM';
  for (let i = 0; i <= 15; i++) { // 5AM to 8PM inclusive
    let displayHour = hour;
    if (hour === 0) displayHour = 12;
    if (hour > 12) {
      displayHour = hour - 12;
      period = 'PM';
    } else if (hour === 12) {
      period = 'PM';
    } else if (hour < 12) {
      period = 'AM';
    }
    slots.push({
      value: `${hour.toString().padStart(2, '0')}:00`,
      label: `${displayHour.toString().padStart(2, '0')}:00 ${period}`
    });
    hour++;
  }
  return slots;
};

const SimpleTimePicker = ({
  selectedTime,
  onChange,
  placeholder = "Select Time",
  disabled = false,
  slots = [],
  loading = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { themeMode, currentTheme } = useTheme();
  const inputRef = useRef(null);
  const [inputWidth, setInputWidth] = useState(undefined);
  const [showNoSlots, setShowNoSlots] = useState(false);

  // Ensure loader is visible for at least 1s before showing 'No slots available'
  useEffect(() => {
    let timer;
    if (loading) {
      setShowNoSlots(false);
    } else if (!loading && slots.length === 0) {
      timer = setTimeout(() => setShowNoSlots(true), 1000);
    } else {
      setShowNoSlots(false);
    }
    return () => clearTimeout(timer);
  }, [loading, slots]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setInputWidth(inputRef.current.offsetWidth);
    }
  }, [isOpen]);

  const handleTimeSelect = (slot) => {
    onChange(slot.slotId);
    setIsOpen(false);
  };

  const getDisplayLabel = (value) => {
    const slot = slots.find(s => s.slotId === value);
    return slot ? slot.displayTime.split(' - ')[0] : placeholder;
  };

  // Animated dots loader
  const AnimatedLoader = () => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 80 }}>
      <div className="animated-dots-loader">
        <span className="dot" />
        <span className="dot" />
        <span className="dot" />
      </div>
      <div style={{ color: currentTheme.callAutomationPage.textColor, opacity: 0.7, marginTop: 8, fontSize: 13 }}>Loading slots...</div>
    </div>
  );

  return (
    <div className={`simple-time-picker ${className}`} style={{ position: 'relative' }}>
      <div
        ref={inputRef}
        className={`picker-input ${themeMode}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          // background: currentTheme.callAutomationPage.dateBackground,
          color: currentTheme.callAutomationPage.textColor,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.7 : 1,
          padding: '10px 16px',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          border: 'none',
          minWidth: '220px',
          fontWeight: 500,
          fontSize: '1rem',
          boxShadow: 'none',
          gap: '10px',
        }}
      >
        <img
          src={themeMode === "dark" ? "/icons/time-dark-theme.png" : "/icons/time-light-theme.png"}
          alt="clock"
          style={{ width: '22px', height: '22px', minWidth: '22px', minHeight: '22px', marginRight: 8 }}
        />
        <span style={{ flex: 1 }}>{getDisplayLabel(selectedTime)}</span>
        <img
          src={isOpen ? (process.env.PUBLIC_URL + '/icons/up-orange.png') : themeMode === "dark"
            ? (process.env.PUBLIC_URL + "/icons/down.svg")
            : (process.env.PUBLIC_URL + "/icons/down-black.png")}
          alt="toggle"
          style={{ width: '18px', height: '18px', marginLeft: 8 }}
        />
      </div>

      {isOpen && !disabled && (
        <div
          className="time-dropdown"
          style={{
            position: 'absolute',
            top: '110%',
            left: 0,
            zIndex: 1000,
            background: currentTheme.callAutomationPage.dateBackground,
            borderRadius: '12px',
            boxShadow: 'none',
            padding: '18px',
            width: inputWidth ? inputWidth : '100%',
            minWidth: 0,
            border: '1.5px solid #444',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 12, color: currentTheme.callAutomationPage.textColor, display: 'flex', alignItems: 'center', gap: 8 }}>
            <img src={themeMode === "dark" ? "/icons/clock.svg" : "/icons/clock.svg"} alt="clock" style={{ width: 18, height: 18 }} />
            Select Time
          </div>
          <div style={{ color: currentTheme.callAutomationPage.textColor, fontSize: 13, marginBottom: 10, opacity: 0.7 }}>
            Choose form available time slots
          </div>
          {loading ? (
            <AnimatedLoader />
          ) : showNoSlots ? (
            <div style={{ textAlign: 'center', color: currentTheme.callAutomationPage.textColor, opacity: 0.7, padding: '20px 0' }}>
              No slots available
            </div>
          ) : (
            <div className="time-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
              {slots.map((slot) => (
                <div
                  key={slot.slotId}
                  onClick={() => handleTimeSelect(slot)}
                  className={selectedTime === slot.slotId ? 'selected' : ''}
                  style={{
                    textAlign: 'center',
                    padding: '10px 0',
                    cursor: 'pointer',
                    background: selectedTime === slot.slotId ? '#FF4E3A' : 'transparent',
                    color: selectedTime === slot.slotId ? '#fff' : currentTheme.callAutomationPage.textColor,
                    borderRadius: '8px',
                    fontWeight: selectedTime === slot.slotId ? 700 : 500,
                    border: selectedTime === slot.slotId ? `2px solid #FF4E3A` : '1.5px solid transparent',
                    transition: 'all 0.2s',
                  }}
                >
                  {slot.displayTime.split(' - ')[0]}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SimpleTimePicker; 